import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import { Text } from '@fluentui/react-components';
import {
  DismissCircle16Filled,
  Checkmark20Regular,
  Dismiss20Regular,
  ImageEdit20Regular,
} from '@fluentui/react-icons';
import {
  MAX_PROMPT_LENGTH,
  HiddenDivForAnnounce,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { ObjectSelectionView } from './object-selection';
import {
  PromptControl,
  PromptControlV2,
} from './prompt-control';
import { useObjectSelection } from '../../model/background-change/use-object-selection';
import { ImageWithShimmer } from '../../../image-with-shimmer';

const HISTORY_LIST_MAX_LENGTH = 10;

const HISTORY_IMAGES_LENGTH = 4;


const ImageToUpdate = (props) => {
  const {
    targetImage,
    imageUrlWithoutBackground,
    i18n,
  } = props;

  return (
    <>
      <ImageWithShimmer
        targetImage={targetImage}
        showShimmer={!imageUrlWithoutBackground}
      />
      <HiddenDivForAnnounce toAnnounce={i18n.getString(_TL_('Enter the image preview view'))} />
    </>
  );
};

const state = {
  ObjectSelection: 'ObjectSelection',
  StaticImage: 'StaticImage',
};

export const usePromptControlViewFactoryWithState = (props) => {
  const {
    i18n,
    image,
    segmentationMasks,
    imageUrlWithoutBackground,
    setSelectedImageSrc,
    selectedMasks,
    setSelectedMasks,
    selectedImageSrc,
    maskValues,
    buttonsRef,
    setNotificationConfig,
    getMaskValueAtPoint,
    logger,
    generateBackground,
    generateBackgrounds,
    imageUrlWithGeneratedBackground,
    setImageUrlWithGeneratedBackground,
    fetchSegmentationError,
    isRemovingBackground,
    isFetchingSegmentations,
    goToImageAdjustView,
    MultiLineEllipsisText,
    applyImage,
    permissions,
  } = props;

  const [showLearnMore, setShowLearnMore] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  // initial error message is null, after generating, it will be set to '' or error message
  const [errorMessage, setErrorMessage] = useState(null);

  const {
    // mask
    borderImageSrc,
    toggleSelectedMask,
    highlightedImageSrc,
    workingSelectedImageSrc,
    updateHighlightedImage,
    updateSubject,
    objectSelectionError,
    startOver,
  } = useObjectSelection({
    originalImageUrl: image.url,
    originalImageWidth: image.width,
    originalImageHeight: image.height,
    segmentationMasks,
    imageUrlWithoutBackground,
    setSelectedImageSrc,
    selectedMasks,
    setSelectedMasks,
    i18n,
  });

  const [activeState, setActiveState] = useState(state.StaticImage);
  const [showRemoved, setShowRemoved] = useState(true);

  const isMultiBackgroundGenerationEnabled = useMemo(() => _.get(permissions, ['dynamic', 'MultiBackgroundGeneration']), [permissions]);

  const dismissNotification = useCallback(() => {
    setNotificationConfig(null);
  }, [setNotificationConfig]);

  const goToStaticImage = useCallback(() => {
    dismissNotification();
    setActiveState(state.StaticImage);
  }, [dismissNotification]);

  const goToObjectSelection = useCallback(() => {
    dismissNotification();
    setActiveState(state.ObjectSelection);
  }, [dismissNotification]);

  const wrappedUpdateSubject = useCallback(() => {
    updateSubject();
    goToStaticImage();
    setShowRemoved(true);
  }, [goToStaticImage, updateSubject]);

  useEffect(() => {
    if (imageUrlWithGeneratedBackground) {
      setShowRemoved(false);
    }
  }, [imageUrlWithGeneratedBackground]);

  useEffect(() => {
    if (prompt && prompt.length > MAX_PROMPT_LENGTH) {
      setErrorMessage(i18n.getString(_TL_('Prompt must be less than 200 characters')));
    } else if (prompt) {
      setErrorMessage('');
    }
  }, [prompt, i18n]);

  const onSelectExampleImage = useCallback((promptText) => {
    if (promptText && promptText !== prompt) {
      setPrompt(promptText);
      logger.logTrace('Copilot change image background - static example image clicked', { promptText });
    }
  }, [logger, prompt]);

  const [historyList, setHistoryList] = useState({
    list: [],
    selectedHistoryIndex: -1,
  });

  const onSelectHistory = useCallback((historyData, index) => {
    const { promptValue: pv, imageUrl } = historyData || {};
    if (pv && imageUrl) {
      setPrompt(pv);
      setHistoryList({ ...historyList, selectedHistoryIndex: index });
    }
  }, [historyList]);

  const onSelectHistoryBackground = useCallback((historyData, index) => {
    const { promptValue: pv, imageUrlList } = historyData || {};
    if (pv && imageUrlList) {
      setPrompt(pv);
      setHistoryList({ ...historyList, selectedHistoryIndex: index });
    }
  }, [historyList]);

  const addNewHistory = useCallback((pPromptValue, generatedImageUrl) => {
    let newHistoryList = [...historyList.list];
    if (newHistoryList.length >= HISTORY_LIST_MAX_LENGTH) {
      newHistoryList = newHistoryList.slice(1);
    }
    newHistoryList.unshift({ promptValue: pPromptValue, imageUrl: generatedImageUrl });
    setHistoryList({
      list: newHistoryList,
      selectedHistoryIndex: 0,
    });
  }, [historyList]);

  const addNewHistoryBackgrounds = useCallback((pPromptValue, generatedImages) => {
    const imageUrls = _.map(generatedImages, generatedImage => generatedImage.imageUrl);
    let newHistoryList = [...historyList.list];
    if (newHistoryList.length >= HISTORY_LIST_MAX_LENGTH) {
      newHistoryList = newHistoryList.slice(1);
    }
    newHistoryList.unshift({ promptValue: pPromptValue, imageUrlList: imageUrls });
    setHistoryList({
      list: newHistoryList,
      selectedHistoryIndex: 0,
    });
  }, [historyList]);

  const generate = useCallback(() => {
    if (logger && logger.logTrace) {
      logger.logTrace('background change: generate button clicked', { prompt });
    }
    setIsGenerating(true);
    setErrorMessage('');
    setShowLearnMore(false);
    generateBackground({
      prompt,
      imageUrl: image.url,
      imageWidth: image.width,
      imageHeight: image.height,
    }).then((imgUrl) => {
      if (logger && logger.logTrace) {
        logger.logTrace('background change: generate success', { prompt, generatedUrl: imgUrl });
      }
      addNewHistory(prompt, imgUrl);
    }).catch((error) => {
      if (logger && logger.logTrace) {
        logger.logTrace('background change: generate image failed', { error, prompt });
      }
      setErrorMessage(i18n.getString(error.uxMessage));
      if (error.availableActions && error.availableActions.gotoDisallowedContent) {
        setShowLearnMore(true);
      }
    }).finally(() => {
      setIsGenerating(false);
    });
  }, [addNewHistory, generateBackground, i18n, image.height, image.url, image.width, logger, prompt]);

  const generateMultiBackgrounds = useCallback(() => {
    if (logger && logger.logTrace) {
      logger.logTrace('backgrounds change: generate button clicked', { prompt });
    }
    setIsGenerating(true);
    setErrorMessage('');
    setShowLearnMore(false);
    generateBackgrounds({
      prompt,
      imageUrl: image.url,
      imageWidth: image.width,
      imageHeight: image.height,
    }).then((images) => {
      if (logger && logger.logTrace) {
        logger.logTrace('backgrounds change: generate backgrounds success', { prompt, generatedImages: images });
      }
      addNewHistoryBackgrounds(prompt, images);
    }).catch((error) => {
      if (logger && logger.logTrace) {
        logger.logTrace('backgrounds change: generate images failed', { error, prompt });
      }
      setErrorMessage(i18n.getString(error.uxMessage));
      if (error.availableActions && error.availableActions.gotoDisallowedContent) {
        setShowLearnMore(true);
      }
    }).finally(() => {
      setIsGenerating(false);
    });
  }, [addNewHistoryBackgrounds, generateBackgrounds, i18n, image.height, image.url, image.width, logger, prompt]);

  const curGeneratedImageUrl = useMemo(() => {
    const {
      list,
      selectedHistoryIndex,
    } = historyList;
    if (isMultiBackgroundGenerationEnabled) {
      const groupIndex = Math.floor(selectedHistoryIndex / HISTORY_IMAGES_LENGTH);
      const imageIndex = selectedHistoryIndex % HISTORY_IMAGES_LENGTH;
      if (groupIndex >= 0 && groupIndex < list.length) {
        const imageGroup = list[groupIndex].imageUrlList;
        if (imageGroup && (imageGroup[imageIndex] !== imageUrlWithGeneratedBackground)) {
          setImageUrlWithGeneratedBackground(imageGroup[imageIndex]);
        }
        return imageGroup[imageIndex];
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (selectedHistoryIndex >= 0 && selectedHistoryIndex < list.length) {
        if (list[selectedHistoryIndex].imageUrl !== imageUrlWithGeneratedBackground) {
          setImageUrlWithGeneratedBackground(list[selectedHistoryIndex].imageUrl);
        }
        return list[selectedHistoryIndex].imageUrl;
      }
    }
    return imageUrlWithGeneratedBackground;
  }, [historyList, imageUrlWithGeneratedBackground, isMultiBackgroundGenerationEnabled, setImageUrlWithGeneratedBackground]);

  const DisallowedContent = useMemo(() => (showLearnMore ? (
    <span className="">
      <a className="HelpTopic clickable" query="help:app54862" href="#!" aria-label="Disallowed content" role="button">
        {i18n.getString(_TL_('Disallowed content'))}
      </a>
    </span>
  ) : null), [i18n, showLearnMore]);

  const renderErrorNotificationConfig = useCallback(({
    message,
    others,
  }) => {
    const content = (
      <div className="notification-content">
        <Text>{message}</Text>
        {others || null}
      </div>
    );
    return ({
      icon: <DismissCircle16Filled className="dismiss-icon" />,
      content,
    });
  }, []);

  useEffect(() => {
    // state 1: fetching segmentation
    if (fetchSegmentationError !== '') {
      setNotificationConfig(renderErrorNotificationConfig({ message: fetchSegmentationError }));
    }
    // state 2: object selection with fetched segmentation
    if (activeState === state.ObjectSelection) {
      if (objectSelectionError && objectSelectionError !== '') {
        setNotificationConfig(renderErrorNotificationConfig({ message: objectSelectionError }));
      } else if (objectSelectionError === '') {
        dismissNotification();
      }
    }
    // state 3: static image with fetched segmentation
    if (!isRemovingBackground && activeState === state.StaticImage && fetchSegmentationError === '') {
      if (errorMessage && errorMessage !== '') {
        setNotificationConfig(renderErrorNotificationConfig({ message: errorMessage, others: DisallowedContent }));
      } else if (errorMessage === '') {
        dismissNotification();
      }
    }
  }, [DisallowedContent, activeState, dismissNotification, errorMessage, fetchSegmentationError, isRemovingBackground, objectSelectionError, renderErrorNotificationConfig, setNotificationConfig]);

  const cancelUpdateSubject = useCallback(() => {
    startOver();
    goToStaticImage();
  }, [goToStaticImage, startOver]);

  const updateImage = useCallback(() => {
    if (logger && logger.logTrace) {
      logger.logTrace('background change: apply new image', { url: imageUrlWithGeneratedBackground });
    }
    applyImage({ url: imageUrlWithGeneratedBackground });
  }, [applyImage, imageUrlWithGeneratedBackground, logger]);

  const cancelBackgroundChange = useCallback(() => {
    if (logger && logger.logTrace) {
      logger.logTrace('background change: cancel');
    }
    goToImageAdjustView();
  }, [goToImageAdjustView, logger]);

  const buttonSettingConfig = {
    [state.ObjectSelection]: [{
      text: i18n.getString(_TL_('Update subject')),
      icon: Checkmark20Regular,
      onClick: wrappedUpdateSubject,
      disabled: objectSelectionError && objectSelectionError !== '',
    }, {
      text: i18n.getString(_TL_('Cancel')),
      icon: Dismiss20Regular,
      onClick: cancelUpdateSubject,
    }],
    [state.StaticImage]: [{
      text: i18n.getString(_TL_('Apply background')),
      icon: Checkmark20Regular,
      onClick: updateImage,
      disabled: isGenerating || isRemovingBackground || (errorMessage && errorMessage !== '') || fetchSegmentationError !== '' || !imageUrlWithGeneratedBackground,
    }, {
      text: i18n.getString(_TL_('Cancel')),
      icon: Dismiss20Regular,
      onClick: cancelBackgroundChange,
    }, {
      text: i18n.getString(_TL_('Edit subject area')),
      icon: ImageEdit20Regular,
      onClick: goToObjectSelection,
      disabled: isGenerating || isRemovingBackground || fetchSegmentationError !== '',
    }],
  };

  const targetImage = useMemo(() => {
    if (showRemoved) {
      return selectedImageSrc || imageUrlWithoutBackground || curGeneratedImageUrl || image.url;
    }
    return curGeneratedImageUrl || selectedImageSrc || imageUrlWithoutBackground || image.url;
  }, [curGeneratedImageUrl, image.url, imageUrlWithoutBackground, selectedImageSrc, showRemoved]);

  const imageSetting = {
    [state.ObjectSelection]: <ObjectSelectionView
      borderImageSrc={borderImageSrc}
      toggleSelectedMask={toggleSelectedMask}
      highlightedImageSrc={highlightedImageSrc}
      workingSelectedImageSrc={workingSelectedImageSrc}
      maskValues={maskValues}
      updateHighlightedImage={updateHighlightedImage}
      updateSubject={wrappedUpdateSubject}
      buttonsRef={buttonsRef}
      originalImageUrl={image.url}
      originalImageHeight={image.height}
      originalImageWidth={image.width}
      getMaskValueAtPoint={getMaskValueAtPoint}
      isFetchingSegmentations={isFetchingSegmentations}
      objectSelectionError={objectSelectionError}
      logger={logger}
      i18n={i18n}
    />,
    [state.StaticImage]: <ImageToUpdate
      i18n={i18n}
      targetImage={targetImage}
      imageUrlWithoutBackground={imageUrlWithoutBackground}
    />,
  };

  const promptControlView = useMemo(() => (isMultiBackgroundGenerationEnabled ? (
    <PromptControlV2
      generate={generateMultiBackgrounds}
      prompt={prompt}
      setPrompt={setPrompt}
      onSelectExampleImage={onSelectExampleImage}
      isGenerating={isGenerating}
      i18n={i18n}
      historyList={historyList.list}
      onSelectHistory={onSelectHistoryBackground}
      selectedHistoryIndex={historyList.selectedHistoryIndex}
      MultiLineEllipsisText={MultiLineEllipsisText}
      disabled={isRemovingBackground || fetchSegmentationError !== ''}
    />
  ) : (
    <PromptControl
      generate={generate}
      prompt={prompt}
      setPrompt={setPrompt}
      onSelectExampleImage={onSelectExampleImage}
      isGenerating={isGenerating}
      i18n={i18n}
      historyList={historyList.list}
      onSelectHistory={onSelectHistory}
      selectedHistoryIndex={historyList.selectedHistoryIndex}
      MultiLineEllipsisText={MultiLineEllipsisText}
      disabled={isRemovingBackground || fetchSegmentationError !== ''}
    />
  )), [MultiLineEllipsisText, fetchSegmentationError, generate, generateMultiBackgrounds, historyList.list, historyList.selectedHistoryIndex, i18n, isGenerating, isRemovingBackground, onSelectExampleImage, onSelectHistory, onSelectHistoryBackground, isMultiBackgroundGenerationEnabled, prompt]);

  return {
    promptControlView,
    buttonsConfig: buttonSettingConfig[activeState],
    imageView: imageSetting[activeState],
  };
};
